import type { FileCopyResponseDatagramBuf } from "~/classes/generated/response"

import { StaticFile } from "~/classes/file/StaticFile"
import { DatagramBuf } from "~/classes/generated/datagram"
import { FileCopyRequestDatagramBuf, RequestDatagramBuf } from "~/classes/generated/request"
import { DatagramBufferBuilder } from "~/utils/flatbuffers/builder"
import { addRequestIdToBuf, parseDatagramError, parseRequestId } from "~/utils/flatbuffers/datagram"
import { addFileToBuf, parseFile } from "~/utils/flatbuffers/file"
import { addFileIdToBuf, parseFileId } from "~/utils/flatbuffers/id"
import { maybe } from "~/utils/helpers"

const _console = useLogger("file/copy")

export function parseFileCopyRequestDatagramBuf(
  datagram: FileCopyRequestDatagramBuf,
): FileCopyRequestDatagram {
  return {
    requestId: parseRequestId(datagram.requestId()!),
    preHash: parseFileId(datagram.preHash()!),
    file: parseFile(datagram.file()!),
    offline: datagram.offline(),
  }
}

export function createFileCopyRequestDatagram(requests: FileCopyRequestDatagram[]) {
  const builder = new DatagramBufferBuilder(64)
  const copies = []
  for (const request of requests) {
    const file = addFileToBuf(builder, request.file)
    const preHash = addFileIdToBuf(builder, request.preHash)
    const requestId = addRequestIdToBuf(builder, request.requestId)

    FileCopyRequestDatagramBuf.startFileCopyRequestDatagramBuf(builder)
    FileCopyRequestDatagramBuf.addRequestId(builder, requestId)
    FileCopyRequestDatagramBuf.addFile(builder, file)
    FileCopyRequestDatagramBuf.addPreHash(builder, preHash)
    FileCopyRequestDatagramBuf.addOffline(builder, request.offline)
    const copyRequest = FileCopyRequestDatagramBuf.endFileCopyRequestDatagramBuf(builder)

    RequestDatagramBuf.startRequestDatagramBuf(builder)
    RequestDatagramBuf.addFileCopyRequest(builder, copyRequest)
    copies.push(RequestDatagramBuf.endRequestDatagramBuf(builder))
  }

  const requestVector = DatagramBuf.createRequestVector(builder, copies)
  DatagramBuf.startDatagramBuf(builder)
  DatagramBuf.addRequest(builder, requestVector)
  builder.finish(DatagramBuf.endDatagramBuf(builder))
  const buf = builder.asUint8Array()
  return buf
}

export function parseFileCopyResponseDatagramBuf(
  datagram: FileCopyResponseDatagramBuf,
): FileCopyResponseDatagram {
  return {
    requestId: parseRequestId(datagram.requestId()!),
    error: maybe(parseDatagramError)(datagram.error()),
    file: maybe(parseFile)(datagram.file()),
    fileId: parseFileId(datagram.fileId()!),
    offline: datagram.offline(),
  }
}

export function sendFileCopyRequests(requests: FileCopyRequestDatagram[]) {
  const { sendWorkerDatagramBuf } = useCache()
  sendWorkerDatagramBuf(createFileCopyRequestDatagram(requests))
}

/** Parse a Flatbuffers representation of a response */
export function handleFileCopyResponseDatagramBuf(datagram: FileCopyResponseDatagramBuf) {
  // TODO: get rid of datagram result, and find out a better way to
  // error out of this, since we need to send notifications
  onFileCopyResponse(parseFileCopyResponseDatagramBuf(datagram))
}

export function onFileCopyResponse(response: FileCopyResponseDatagram) {
  const { emitInterfaceEvent, emitEntityEvent } = useEvents()

  emitInterfaceEvent("fileCopyResponse", response, response.requestId)

  if (response.error)
    return onFileCopyErrored(response.error)
  if (!response.file)
    throw new Error("No file in copy response")

  const iFile = new StaticFile(response.file)
  const parentId = iFile.inferredParentId()
  if (!parentId) {
    console.error("No parent id in file create response", response)
    throw new Error("no parent id in file create response")
  }

  emitEntityEvent("fileProperties", iFile.id, iFile.inner(), response.offline)
  emitEntityEvent("fileCreated", iFile.id, iFile.inner(), response.offline)
  emitEntityEvent("childProperties", parentId, iFile.inner(), response.offline)
  emitEntityEvent("childCreated", parentId, iFile.inner(), response.offline)
}

export function onFileCopyErrored(error: DatagramError,
) {
  const { notifyDatagramError } = useAlerts()
  return notifyDatagramError(error, "File copy was unsuccessful")
}
